import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { EnvironmentService } from '@core/feature';
import { DeliveryZone } from '@delivery/const';
import { DeliveryCalculatorFormService, DeliveryZonesService } from '@delivery/feature';
import { DeviceService } from '@shared/services';
import { ModalService } from '@spartacus/storefront';
import { GeocodeResult } from '@yandex-maps/const';
import { YmapsService } from '@yandex-maps/feature';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { DeliveryCalculatorResultsModalComponent } from '../delivery-calculator-results-modal/delivery-calculator-results-modal.component';

@Component({
  selector: 'ui-delivery-map',
  templateUrl: './delivery-map.component.html',
  styleUrls: ['./delivery-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeliveryMapComponent implements OnInit, OnDestroy {
  static POLYGON_COLORS = { FREE_DELIVERY: '#0e4779', PAID_DELIVERY: '#ffda33' };
  zones$: Observable<DeliveryZone[]>;
  geoObjects$: Observable<ymaps.GeoObject[]>;
  mapInstance: ymaps.Map;
  initialized = false;

  addressGeoObject$ = new Subject<GeocodeResult | undefined>();

  private subscription = new Subscription();

  constructor(
    private deliveryCalculatorFormService: DeliveryCalculatorFormService,
    private modalService: ModalService,
    private deliveryZonesService: DeliveryZonesService,
    public deviceService: DeviceService,
    protected ymapsService: YmapsService,
    private environmentService: EnvironmentService,
  ) {}

  ngOnInit(): void {
    if (!this.environmentService.isPlatformBrowser) {
      return;
    }

    this.initialized = true;

    this.geoObjects$ = this.ymapsService.isLoaded$.pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        this.getZones().pipe(
          map((zones) =>
            zones?.map(
              (zone: DeliveryZone) =>
                new ymaps.GeoObject(
                  {
                    geometry: new ymaps.geometry.Polygon(zone.polygon.coordinates, 'nonZero'),
                  },
                  {
                    fill: true,
                    fillColor: zone.hasFreeDelivery
                      ? DeliveryMapComponent.POLYGON_COLORS.FREE_DELIVERY
                      : DeliveryMapComponent.POLYGON_COLORS.PAID_DELIVERY,
                    fillOpacity: 0.3,
                    strokeColor: zone.hasFreeDelivery
                      ? DeliveryMapComponent.POLYGON_COLORS.FREE_DELIVERY
                      : DeliveryMapComponent.POLYGON_COLORS.PAID_DELIVERY,
                  },
                ),
            ),
          ),
        ),
      ),
    );

    this.subscription.add(
      this.addressGeoObject$.pipe(filter<GeocodeResult>(Boolean)).subscribe((address) => {
        const coords = address.geometry.getCoordinates();

        this.mapInstance.setCenter(coords);
        this.mapInstance.setZoom(13);

        this.mapInstance.geoObjects.each((geoObject) => {
          if (geoObject.options.get('id', {}) === 'userPlacemark') {
            this.mapInstance.geoObjects.remove(geoObject);
          }
        }, {});

        this.mapInstance.geoObjects.add(
          new ymaps.Placemark(
            coords,
            { iconCaption: address.getAddressLine().split(',').slice(2).join(',') },
            { id: 'userPlacemark', preset: 'islands#orangeCircleDotIcon' },
          ),
        );
      }),
    );
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.mapInstance = ymap;
    this.mapInstance.events.remove('click', this.onMapClick);
    this.mapInstance.geoObjects.events.remove('click', this.onMapClick);

    this.mapInstance.geoObjects.events.add('click', this.onMapClick);
    this.mapInstance.events.add('click', this.onMapClick);
  }

  ngOnDestroy(): void {
    this.mapInstance?.events.remove('click', this.onMapClick);
    this.mapInstance?.geoObjects.events.remove('click', this.onMapClick);
    this.subscription.unsubscribe();
  }

  onMapClick = (e: ymaps.Event): void => {
    const coords = e.get<ymaps.Coordinate>('coords');

    this.deliveryCalculatorFormService.loadTimeSlots({ longitude: coords[1], latitude: coords[0] });

    this.modalService.open(DeliveryCalculatorResultsModalComponent, { centered: true, size: 'md' });
  };

  onChangeAddress(geocodeResult?: GeocodeResult): void {
    this.addressGeoObject$.next(geocodeResult);
  }

  private getZones(): Observable<DeliveryZone[]> {
    return this.deliveryZonesService.deliveryZones$;
  }
}
